/* eslint-disable camelcase */
/**
 * @file 现货网格相关store
 */
import { commonapi } from '@/apis/api2';
import { botApi } from '@/apis/spot';
import { ls } from '@sfe/bone-cache';
import { i18n } from '@/locales/i18n';
import { error } from '@/utils/toast';

let getSymbolTimer = null;

const state = {
    symbols: [], // 网格交易对
    tab: 'standard', // 当前选择tab 'standard': 标准 | 'strategy': 策略
    isShowAllPairs: ls.get('SPOT_bot_show_all') === 'Y' || false, // 是否展示全部交易对
    openLoading: true,
    runningList: [], // 运行中的网格
    historyList: [], // 历史网格
    validateBot: {}, // 网格验证数据
    // 网格画线数据
    gridLine: {
        max_price: '0',
        min_price: '0',
        grid_number: 0,
        support: false,
        type: 'grade',
    },
};

const getters = {
    // 交易对map
    symbolsMap(state) {
        const map = {};

        state.symbols.forEach((item) => {
            const symbol = `${item.base_token}${item.quote_token}`;
            map[symbol] = item;
        });

        return map;
    },
    // 当前选择网格交易对
    currentSymbol(_, { symbolsMap }, rootState) {
        return symbolsMap[rootState.exchange.symbolId];
    },
    validateCode() {
        const { t } = i18n.global;
        return {
            SPOT_CHECK_CODE_LOW_PRICE_TOO_LOW: t('botValidateErrCode101'), // 最小价格太低，如<0等
            SPOT_CHECK_CODE_LOW_PRICE_TOO_HIGH: t('botValidateErrCode102'), // 最小价格太高，如>high_price或>合约价格上限
            SPOT_CHECK_CODE_HIGH_PRICE_TOO_LOW: t('botValidateErrCode103'), // 最大价格太低，如<0或<low_price
            SPOT_CHECK_CODE_HIGH_PRICE_TOO_HIGH: t('botValidateErrCode104'), // 最大价格太高，如>合约价格上限
            SPOT_CHECK_CODE_GRID_NO_TOO_LOW: t('botValidateErrCode105'), // 网格数太少，如 <2
            SPOT_CHECK_CODE_GRID_NO_TOO_HIGH: t('botValidateErrCode106'), // 网格数太多
            SPOT_CHECK_CODE_INVESTMENT_TOO_LOW: t('botValidateErrCode107'), // 投资额太低，如不满足投资要求
            SPOT_CHECK_CODE_INVESTMENT_TOO_HIGH: t('botValidateErrCode108'), // 投资额太高，如大于合约投资上限
            SPOT_CHECK_CODE_TP_TOO_LOW: t('botValidateErrCode109'), // 止盈太低
            SPOT_CHECK_CODE_TP_TOO_HIGH: t('botValidateErrCode110'), // 止盈太高
            SPOT_CHECK_CODE_SL_TOO_LOW: t('botValidateErrCode111'), // 止损太低
            SPOT_CHECK_CODE_SL_TOO_HIGH: t('botValidateErrCode112'), // 止损太高
            SPOT_CHECK_CODE_EP_TOO_LOW: t('botValidateErrCode113'), // 入场价太低
            SPOT_CHECK_CODE_EP_TOO_HIGH: t('botValidateErrCode114'), // 入场价太高
        };
    },
    // 创建网格错误code
    createErrorCode() {
        const { t } = i18n.global;
        return {
            400: t('createErrorCode400'), // 客户端发送的数据包含非法参数。查看错误消息和错误详情来获取更多的信息
            401: t('createErrorCode401'), // 余额不足
            402: t('createErrorCode402'), // 用户创建的机器人数已达上限
            403: t('createErrorCode403'), // FORBIDDEN
            404: t('createErrorCode404'), // 特定的资源没有被找到
            407: t('createErrorCode407'), // 该用户类型不支持创建机器人子账号，例如普通子账号
            408: t('createErrorCode408'), // 选择了重复定投标的(symbol)
            409: t('createErrorCode409'), // 非法的最大投资额
            410: t('createErrorCode410'), // 非法的最大投资额
            411: t('createErrorCode411'), // bot已经被关闭
            412: t('createErrorCode412'), // 统保账户升级中
            500: t('createErrorCode500'), // 服务器内部错误
            501: t('createErrorCode501'), // 内部处理超时
            503: t('createErrorCode503'), // 当前DCA Bot的状态不支持关闭，可能正在进行定投
        };
    },
};

const mutations = {
    // 设置网格交易对
    SET_SYMBOLS(state, symbols) {
        state.symbols = symbols;
    },
    // 更新tab
    UPDATE_TAB(state, tab) {
        state.tab = tab;
    },
    SET_SHOW_ALL_PAIRS(state, payload) {
        state.isShowAllPairs = payload;
    },
    SET_RUNNING_LIST(state, payload) {
        state.openLoading = false;
        state.runningList = payload;
    },
    SET_HISTORY_LIST(state, payload) {
        state.openLoading = false;
        state.historyList = payload;
    },
    // 设置交验数据
    SET_VALIDATE_BOT(state, payload) {
        state.validateBot = payload;
    },
    // 更新网格线数据
    UPDATE_GRID_LINE(state, payload) {
        state.gridLine = { ...state.gridLine, ...payload };
    },
    // 重置网格线数据
    RESET_GRID_LINE(state) {
        state.gridLine = {
            max_price: '0',
            min_price: '0',
            grid_number: 0,
            support: false,
            type: 'grade',
        };
    },
};

const actions = {
    // 获取网格交易对
    async get_bot_symbols({
        state, getters, commit, dispatch,
    }, isGetValidate = true) {
        if (isGetValidate) commit('SET_VALIDATE_BOT', {});

        const res = await commonapi.getTradeBotList();

        // eslint-disable-next-line camelcase
        if (res.success && res.result?.symbol_info?.length) {
            commit('SET_SYMBOLS', res.result.symbol_info);

            if (state.tab === 'strategy') {
                if (getters.currentSymbol) {
                    clearTimeout(getSymbolTimer);
                    getSymbolTimer = setTimeout(() => {
                        dispatch('get_bot_symbols', false);
                    }, 5000);
                }

                if (isGetValidate) {
                    const {
                        symbol,
                        min_price,
                        max_price,
                        cell_number,
                    } = getters.currentSymbol;

                    // eslint-disable-next-line no-param-reassign
                    const params = {
                        symbol,
                        cell_number,
                        min_price,
                        max_price,
                    };
                    dispatch('validate', { params });
                }
            }
        }
    },
    // 校验
    validate({ getters, commit }, { params, showToast }) {
        return new Promise((resolve) => {
            botApi.validate(params).then(({ success, result, message }) => {
                if (success && result) {
                    result.success = result?.check_code === 'SPOT_CHECK_CODE_SUCCESS_UNSPECIFIED';

                    commit('SET_VALIDATE_BOT', result);
                }

                if (showToast) {
                    if (!result.success && getters.validateCode[result.check_code]) {
                        error(getters.validateCode[result.check_code]);
                    } else if (!success) {
                        error(message);
                    }
                }

                resolve(result);
            });
        });
    },
    // 获取运行中的网格
    async GET_GRIDS_LIST({ state, rootState, commit }, { status } = {}) {
        const { symbolId } = rootState.exchange;
        if (!symbolId) return;
        const res = await botApi.getGridList({
            page: 0,
            limit: 100,
            status,
            symbol: state.isShowAllPairs ? undefined : symbolId,
        });
        // eslint-disable-next-line camelcase
        if (res.success && res.result?.grids) {
            if (status === 0) {
                commit('SET_RUNNING_LIST', res.result.grids);
            }
            if (status === 1) {
                commit('SET_HISTORY_LIST', res.result.grids);
            }
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
